import { useState } from "react";

import { adminApi, type BreakGlassOutcome } from "../api";

type Action =
  | "freeze-all"
  | "freeze-project"
  | "unfreeze-project"
  | "revoke-keys"
  | "protect-snapshots"
  | "force-scan"
  | "start-recovery";

const NEEDS_PROJECT: Record<Action, boolean> = {
  "freeze-all": false,
  "freeze-project": true,
  "unfreeze-project": true,
  "revoke-keys": true,
  "protect-snapshots": true,
  "force-scan": true,
  "start-recovery": true,
};

const DESCRIPTIONS: Record<Action, string> = {
  "freeze-all": "Freeze every project platform-wide. Only writes/deletes/shares are blocked.",
  "freeze-project": "Freeze one project. Reads stay available for investigation.",
  "unfreeze-project": "Lift a freeze on one project.",
  "revoke-keys": "Revoke every active API key for one project.",
  "protect-snapshots": "Extend WORM retention on a project's backup copies (legal hold).",
  "force-scan": "Run an on-demand checksum-anchored integrity scan for one project.",
  "start-recovery":
    "Emergency, project-wide point-in-time restore. Read-only — never overwrites live data " +
    "except by re-writing an already-verified backup's bytes back onto a primary.",
};

/**
 * 🆘 Break-Glass — भाग E4's highest-risk lever. Every action requires a
 * reason and an explicit, freshly-confirmed step-up acknowledgement before
 * the button even becomes clickable — the same "reason + re-auth + audit"
 * discipline the Admin API itself enforces server-side
 * (`application.break_glass.require_break_glass_auth`). There is
 * deliberately no "Delete all" here, and never will be: nothing on this
 * page can reach a permanent-delete path (see `application.break_glass`'s
 * module docstring) — the one operation that can is gated behind the
 * separate, two-operator dual-auth workflow, not a checkbox on this form.
 */
export function BreakGlass(): JSX.Element {
  const [action, setAction] = useState<Action>("freeze-project");
  const [reason, setReason] = useState("");
  const [projectId, setProjectId] = useState("");
  const [stepUpAck, setStepUpAck] = useState(false);
  const [extendDays, setExtendDays] = useState(90);
  const [asOf, setAsOf] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<unknown>(null);

  const canSubmit = reason.trim().length > 0 && stepUpAck && (!NEEDS_PROJECT[action] || projectId);

  async function submit(): Promise<void> {
    if (!canSubmit) return;
    if (!window.confirm(`Confirm: ${action} — "${reason}". This cannot be undone by this form.`)) {
      return;
    }
    setBusy(true);
    setError(null);
    setResult(null);
    const body: Record<string, unknown> = { reason, step_up_verified: stepUpAck };
    if (NEEDS_PROJECT[action]) body.project_id = projectId;
    if (action === "protect-snapshots") body.extend_days = extendDays;
    if (action === "start-recovery") body.as_of = asOf;

    try {
      const outcome = await adminApi.post<BreakGlassOutcome | unknown>(
        `/api/v1/admin/break-glass/${action}`,
        body,
      );
      setResult(outcome);
      setStepUpAck(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h2 className="page-title">🆘 Break Glass</h2>
      <p className="page-subtitle">
        Freeze / revoke / protect / scan / recover — never delete. Every action is audited even
        when refused.
      </p>
      {error && <div className="error-banner">{error}</div>}

      <div className="panel">
        <div className="form-row">
          <label htmlFor="action">Action</label>
          <select id="action" value={action} onChange={(e) => setAction(e.target.value as Action)}>
            {Object.keys(NEEDS_PROJECT).map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
        <p className="page-subtitle">{DESCRIPTIONS[action]}</p>

        {NEEDS_PROJECT[action] && (
          <div className="form-row">
            <label htmlFor="project">Project ID</label>
            <input id="project" value={projectId} onChange={(e) => setProjectId(e.target.value)} />
          </div>
        )}
        {action === "protect-snapshots" && (
          <div className="form-row">
            <label htmlFor="days">Extend (days)</label>
            <input
              id="days"
              type="number"
              min={1}
              max={3650}
              value={extendDays}
              onChange={(e) => setExtendDays(Number(e.target.value))}
            />
          </div>
        )}
        {action === "start-recovery" && (
          <div className="form-row">
            <label htmlFor="asof">Restore as of</label>
            <input
              id="asof"
              type="datetime-local"
              value={asOf}
              onChange={(e) => setAsOf(e.target.value)}
            />
          </div>
        )}
        <div className="form-row">
          <label htmlFor="reason">Reason (required)</label>
          <textarea
            id="reason"
            rows={2}
            style={{ flex: 1, minWidth: 300 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. ransomware incident #42 — freezing pending investigation"
          />
        </div>
        <div className="form-row">
          <label htmlFor="stepup">
            <input
              id="stepup"
              type="checkbox"
              checked={stepUpAck}
              onChange={(e) => setStepUpAck(e.target.checked)}
              style={{ width: "auto", marginRight: 6 }}
            />
          </label>
          <span style={{ fontSize: 13 }}>
            I have just completed a fresh step-up re-authentication for this action.
          </span>
        </div>
        <button className="danger" disabled={!canSubmit || busy} onClick={submit}>
          {busy ? "Executing…" : `Execute ${action}`}
        </button>
      </div>

      {result !== null && (
        <div className="panel">
          <h3>Result</h3>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
